import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { CloseIcon } from "./icons";
import styles from "./Toast.module.css";

/**
 * How long a toast without an action stands on screen, not counting the time
 * it is held. A toast with an action has no such limit (see `ToastItem`).
 */
export const TOAST_MS = 3200;
/**
 * How long a leaving toast keeps its place while it fades, when the page does
 * not say otherwise: the same 140 ms as `--dur-2` in tokens.css, which the
 * stylesheet's fade uses; a test holds the two together. The removal waits for
 * a timer, not for the stylesheet's `transitionend`, which reduced motion or a
 * hidden tab can swallow — but the timer's length follows the fade's:
 * `exitDelayMs` reads `--dur-2` off the root, so the motion regulator on
 * `/_muster` (`data-bewegung="langsam"`, every duration ×4) cannot outlast the
 * element. This is only the fallback for a page where that is empty.
 */
export const TOAST_EXIT_MS = 140;
/** More than this and the oldest goes; a person reads one or two, not a pile. */
const MAX_VISIBLE = 3;

interface ToastBase {
  /** The whole message, in words: it is what a screen reader hears, and the tone adds nothing to it. */
  text: string;
  /** `konflikt` marks the leading edge in the status red; default `neutral`. */
  tone?: "neutral" | "konflikt";
}

/** A toast that is heard as well as seen — the host's live region reads it out. */
interface HeardToast extends ToastBase {
  /**
   * An undo-shaped follow-up: runs `onAction`, then the toast goes. A toast
   * with an action does not go by itself — a keyboard user needs longer than
   * `TOAST_MS` to reach it — and gets a close button beside the action.
   */
  action?: { label: string; onAction: () => void };
  announce?: true;
}

/**
 * A toast for the eye only (Welle 1, idea 23): the screen's own live line
 * already says the same sentence, and a screen reader would hear it twice.
 * Hidden from the accessibility tree, so it carries no action — a button a
 * screen reader cannot see is a button it cannot reach.
 */
interface SeenToast extends ToastBase {
  action?: undefined;
  announce: false;
}

export type ToastInput = HeardToast | SeenToast;

export interface ToastApi {
  /** Puts a toast up and returns its id. */
  show(toast: ToastInput): string;
  /** Sends the toast with this id away; an id that is not there (any more) does nothing. */
  dismiss(id: string): void;
}

interface ToastEntry {
  id: string;
  text: string;
  action?: HeardToast["action"];
  tone: "neutral" | "konflikt";
  /** `false`: seen, not heard — see `SeenToast`. */
  announce: boolean;
  /** Going: still in the DOM while the fade lasts (`exitDelayMs`) so it can be seen. */
  leaving: boolean;
}

/** What the host needs and a caller of `useToast` does not: the list itself. */
interface ToastList {
  toasts: readonly ToastEntry[];
  dismiss: (id: string) => void;
  /** Takes a toast out for good — called when its exit is over. */
  remove: (id: string) => void;
}

const ApiContext = createContext<ToastApi | null>(null);
const ListContext = createContext<ToastList | null>(null);

/** Whether the person asked for less motion. jsdom has no `matchMedia`, and neither does an old browser. */
function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * How long this toast's fade lasts now: `--dur-2` on the root, read at the
 * moment it leaves so a regulator switched a second ago counts. `ms` and `s`
 * are understood; anything else — an empty value (jsdom loads no stylesheet),
 * a `calc()`, a negative — gives `TOAST_EXIT_MS`.
 */
function exitDelayMs(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--dur-2").trim();
  const match = /^(\d*\.?\d+)(ms|s)$/.exec(raw);
  if (match === null) return TOAST_EXIT_MS;
  const delay = Number(match[1]) * (match[2] === "s" ? 1000 : 1);
  return Number.isFinite(delay) ? delay : TOAST_EXIT_MS;
}

/**
 * Keeps `MAX_VISIBLE` toasts that are not leaving: the oldest ones beyond
 * that are sent away — faded out, or at once when `instant`.
 */
function capVisible(list: readonly ToastEntry[], instant: boolean): readonly ToastEntry[] {
  let excess = list.filter((entry) => !entry.leaving).length - MAX_VISIBLE;
  if (excess <= 0) return list;
  const kept: ToastEntry[] = [];
  for (const entry of list) {
    if (excess > 0 && !entry.leaving) {
      excess -= 1;
      if (!instant) kept.push({ ...entry, leaving: true });
    } else {
      kept.push(entry);
    }
  }
  return kept;
}

/**
 * The toasts of one screen tree (BW-9): a short note that something happened,
 * with at most one way back — "Rückgängig". It holds the list and nothing
 * else; every timer belongs to the toast it times (`ToastItem`), so a toast
 * that goes takes its timer along. `useToast` is stable: a caller does not
 * re-render when the list changes.
 *
 * Put a `ToastHost` inside it — that is where the toasts are drawn.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastEntry[]>([]);
  const counter = useRef(0);

  const show = useCallback((input: ToastInput): string => {
    counter.current += 1;
    const id = `toast-${counter.current}`;
    const entry: ToastEntry = {
      id,
      text: input.text,
      action: input.action,
      tone: input.tone ?? "neutral",
      announce: input.announce !== false,
      leaving: false,
    };
    const instant = prefersReducedMotion();
    setToasts((list) => capVisible([...list, entry], instant));
    return id;
  }, []);

  const dismiss = useCallback((id: string): void => {
    const instant = prefersReducedMotion();
    setToasts((list) => {
      const entry = list.find((candidate) => candidate.id === id);
      // Not there, or already on its way out: nothing to do.
      if (entry === undefined || entry.leaving) return list;
      if (instant) return list.filter((candidate) => candidate.id !== id);
      return list.map((candidate) => (candidate.id === id ? { ...candidate, leaving: true } : candidate));
    });
  }, []);

  const remove = useCallback((id: string): void => {
    setToasts((list) => list.filter((candidate) => candidate.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);
  const list = useMemo<ToastList>(() => ({ toasts, dismiss, remove }), [toasts, dismiss, remove]);

  return (
    <ApiContext.Provider value={api}>
      <ListContext.Provider value={list}>{children}</ListContext.Provider>
    </ApiContext.Provider>
  );
}

/** `show` and `dismiss` for the nearest `ToastProvider`. */
export function useToast(): ToastApi {
  const api = useContext(ApiContext);
  if (api === null) throw new Error("useToast must be used inside a <ToastProvider>.");
  return api;
}

/** Where no provider is: a toast taken and dropped. One object, so a caller's dependencies hold still. */
const NO_TOASTS: ToastApi = { show: () => "", dismiss: () => {} };

/**
 * The shell's toasts, for a screen (Welle 1, idea 23): `useToast`, except
 * that a screen rendered without the shell — its own tests, a page outside
 * it — gets a sink instead of an error. A toast there has nobody to see it;
 * what the screen says on itself (its notices, its live line) still stands,
 * and no screen may depend on a toast for a word nobody else says.
 */
export function useScreenToast(): ToastApi {
  return useContext(ApiContext) ?? NO_TOASTS;
}

function useToastList(): ToastList {
  const list = useContext(ListContext);
  if (list === null) throw new Error("<ToastHost> must be rendered inside a <ToastProvider>.");
  return list;
}

/** What `focusin` reports as the element focus came from, if it is something that can take it back. */
function focusable(target: EventTarget | null): HTMLElement | SVGElement | null {
  return target instanceof HTMLElement || target instanceof SVGElement ? target : null;
}

interface ToastItemProps {
  entry: ToastEntry;
  /** The host is held — the pointer is on it or the focus is in it — and so is every timer. */
  paused: boolean;
  dismiss: (id: string) => void;
  remove: (id: string) => void;
  /** Hands the focus back to where it was before it moved into the host. */
  release: () => void;
}

/**
 * One toast, and the two timers that belong to it: its life (`TOAST_MS`,
 * which stops while `paused` and goes on with what was left) and its exit
 * (as long as the fade lasts, `exitDelayMs`). Both are cleared the moment the
 * toast or the provider goes, so nothing fires into an unmounted tree.
 *
 * Only a toast *without* an action has a life. One with an action stays until
 * its action runs, its close button or Escape sends it away, or a fourth toast
 * pushes it out: the action is the one thing on a toast a person has to reach,
 * and a keyboard user reaches it by Tab, which can take longer than 3.2 s
 * (WCAG 2.2.1 — no time limit on what must be reached). The close button is
 * there because a toast that waits has to be able to be put away.
 */
function ToastItem({ entry, paused, dismiss, remove, release }: ToastItemProps) {
  const { id, text, action, tone, announce, leaving } = entry;
  const ref = useRef<HTMLDivElement>(null);
  const remaining = useRef(TOAST_MS);

  // The life, for a toast without an action. A hold keeps what was left
  // rather than starting over: a person who reads for three seconds, then
  // points at it, gets the last 200 ms.
  const mortal = action === undefined;
  useEffect(() => {
    if (!mortal || leaving || paused) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => dismiss(id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - Math.max(0, Date.now() - startedAt));
    };
  }, [mortal, leaving, paused, id, dismiss]);

  // The exit.
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => remove(id), exitDelayMs());
    return () => clearTimeout(timer);
  }, [leaving, id, remove]);

  // A toast that goes while the focus is on its button — Escape, the action,
  // a newer toast pushing it out, a `dismiss` from elsewhere — hands the
  // focus back first. Without it the focus would end on `<body>` and a
  // keyboard user would start again from the top of the page. On `leaving`,
  // not only on removal: during the fade the button is still on screen.
  useLayoutEffect(() => {
    if (leaving && ref.current?.contains(document.activeElement)) release();
  }, [leaving, release]);
  useLayoutEffect(() => {
    // The element is read now: React has let go of the ref by the time a cleanup runs.
    const element = ref.current;
    return () => {
      if (element?.contains(document.activeElement)) release();
    };
  }, [release]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    // Handled here: a dialog behind the host closes on an Escape nobody
    // swallowed, and would take the page's context with it.
    event.preventDefault();
    event.stopPropagation();
    dismiss(id);
  };

  const onAction = () => {
    // First what the person asked for — it may place the focus itself — then the toast.
    action?.onAction();
    dismiss(id);
  };

  return (
    <div
      ref={ref}
      className={styles.toast}
      data-tone={tone}
      data-leaving={leaving ? "true" : undefined}
      // Seen, not heard: the screen's own live line says it (`SeenToast`).
      aria-hidden={announce ? undefined : true}
      onKeyDown={onKeyDown}
    >
      <span className={styles.text}>{text}</span>
      {action ? (
        <span className={styles.tools}>
          <button type="button" className={styles.action} onClick={onAction}>
            {action.label}
          </button>
          <button
            type="button"
            className={styles.close}
            aria-label="Meldung schließen"
            title="Schließen"
            onClick={() => dismiss(id)}
          >
            <CloseIcon />
          </button>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Where the toasts are drawn (BW-9): at the bottom of the window, over the
 * content, on the toast layer. Always in the page, empty or not — one polite
 * live region, so a screen reader that has already registered it hears each
 * toast's text when it is put in (a region created together with its text is
 * skipped). Portalled to `document.body`: a `Dialog` makes `#root` inert, and
 * a toast shown while one is open must stay reachable.
 *
 * Holding: while the pointer is on the host or the focus is in it, the timer
 * of every toast that has one stops — time enough to read a note while the
 * person is looking at it. It is a courtesy to the plain toast, not what makes
 * the action reachable: a toast with an action has no timer at all (`ToastItem`).
 * Leaving lets the others go on with what was left. The host is the stack
 * itself, so crossing the gap between two toasts is still "on the host". A
 * hold by the pointer does not outlive the last toast: with nothing under it,
 * some browsers send no `pointerleave`, and the next toast would wait for a
 * pointer that is gone.
 *
 * Focus: the element that had it before it entered the host is remembered
 * (`relatedTarget` of the first `focusin` from an element outside) and gets it
 * back when the focused toast goes. With no such element the focus is let go
 * rather than left on a toast that is about to vanish. What is remembered is
 * dropped as soon as it has been used or the focus has left for good — and a
 * `focusin`/`focusout` with no `relatedTarget` while the button is still the
 * active element is the window going away and coming back, which neither
 * changes where the person came from nor releases the hold.
 */
export function ToastHost() {
  const { toasts, dismiss, remove } = useToastList();
  const hostRef = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | SVGElement | null>(null);
  const [hovering, setHovering] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);

  // Run empty: whatever the pointer was resting on is gone, and the hold with it.
  // Set during the render, not in an effect — React renders again at once, and
  // the condition is false the second time.
  if (toasts.length === 0 && hovering) setHovering(false);

  const release = useCallback(() => {
    const target = returnTo.current;
    // Used up: a later entry from nowhere must not be taken back to this.
    returnTo.current = null;
    if (target !== null && target.isConnected) target.focus();
    // Still in the host — the target could not take it (gone, hidden, inert).
    const holder = document.activeElement;
    if (holder instanceof HTMLElement && hostRef.current?.contains(holder)) holder.blur();
  }, []);

  const onFocus = (event: FocusEvent<HTMLDivElement>) => {
    // Only a way in from an element outside remembers. From another toast it
    // does not (the first way in stands), and neither does a `focusin` from
    // nowhere — the window coming back, or nothing having had the focus — which
    // says nothing about where the person was.
    const from = event.relatedTarget;
    if (from instanceof Element && !event.currentTarget.contains(from)) returnTo.current = focusable(from);
    setFocusWithin(true);
  };
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const to = event.relatedTarget;
    if (to instanceof Node && event.currentTarget.contains(to)) return;
    // No `relatedTarget` and the button still the active element: the window
    // lost the focus, the person did not. The hold stays.
    if (to === null && event.currentTarget.contains(document.activeElement)) return;
    setFocusWithin(false);
    // The focus has gone for good, to another element or to the bare page:
    // wherever the person was before the toast is no longer where they are.
    returnTo.current = null;
  };

  const paused = hovering || focusWithin;

  return createPortal(
    <div
      ref={hostRef}
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className={styles.host}
      onPointerEnter={() => setHovering(true)}
      onPointerLeave={() => setHovering(false)}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {toasts.map((entry) => (
        <ToastItem key={entry.id} entry={entry} paused={paused} dismiss={dismiss} remove={remove} release={release} />
      ))}
    </div>,
    document.body,
  );
}
